import type { Rng } from '../rng';
import { FRAME_MS, drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { ActResult, CasinoGame, TableEnv } from './types';

/**
 * Horse Racing: six horses with fixed chances; bet on one to win. Odds are
 * the fair price minus a 5% takeout (real tracks take ~15%; lowered from 10%
 * on 2026-09-23). The winner is drawn when the race
 * starts; the race then plays out on the lens over RACE_MS (driven by the
 * real-time tick, like Crash) and settles when it crosses the line. You're
 * locked in until it finishes.
 */
// 5% measured ~94.2% as played; 3.5% lifts it to ~96% (balance patch,
// 2026-09-23).
export const TAKEOUT = 0.035;
export const RACE_MS = 4000;
const BAR = 10;

export const HORSES: readonly { name: string; chance: number }[] = [
  { name: 'Lucky Star', chance: 0.3 },
  { name: 'Blue Moon', chance: 0.24 },
  { name: 'Iron Duke', chance: 0.18 },
  { name: 'Dark Horse', chance: 0.13 },
  { name: 'Wild Card', chance: 0.09 },
  { name: 'Long Shot', chance: 0.06 },
];

export function horseOdds(i: number, takeout: number = TAKEOUT): number {
  return (1 - takeout) / HORSES[i]!.chance;
}

/** Backing horse `i`: its results (for the ★'s second-chance rate). */
export function horseOutcomes(i: number): Outcome[] {
  const p = HORSES[i]!.chance;
  return [
    [p, horseOdds(i)],
    [1 - p, 0],
  ];
}

export interface HorseState {
  phase: 'idle' | 'racing' | 'done';
  pick: number;
  winner: number;
  /** Where each horse finishes, as a share of the track (winner = 1). */
  finish: number[];
  startedAt: number;
  /** Race progress step on the lens (0-BAR); only changes redraw. */
  shown: number;
  bet: number;
  takeout: number;
  returned: number;
  /** Long Shot wins in a row while backing it (the jackpot needs two). */
  longshotStreak: number;
  /** The ★'s second chance re-ran a lost race. */
  lucky: boolean;
}

function drawWinner(rng: Rng): number {
  let roll = rng.next();
  for (let i = 0; i < HORSES.length; i++) {
    roll -= HORSES[i]!.chance;
    if (roll < 0) return i;
  }
  return HORSES.length - 1;
}

function settle(state: HorseState, env: TableEnv): ActResult<HorseState> {
  const won = state.pick === state.winner;
  const returned = won ? Math.round(state.bet * horseOdds(state.pick, state.takeout)) : 0;
  const longshotStreak = state.pick === HORSES.length - 1 ? (won ? state.longshotStreak + 1 : 0) : 0;
  return {
    state: { ...state, phase: 'done', shown: BAR, returned, longshotStreak },
    bankroll: env.bankroll + returned,
    settled: { wagered: state.bet, returned },
    jackpot: longshotStreak >= 2,
  };
}

function track(state: HorseState, step: number): string {
  return HORSES.map((h, i) => {
    const filled = Math.min(BAR, Math.round(state.finish[i]! * step));
    const mark = state.pick === i ? '>' : ' ';
    return `${mark}${h.name.padEnd(10)} ${'▮'.repeat(filled)}${'▯'.repeat(BAR - filled)}`;
  }).join('\n');
}

export const horseRacing: CasinoGame<HorseState> = {
  id: 'horses',
  noDealer: true,
  usesLuck: true,
  name: 'Horse Racing',
  blurb: 'Six horses, fixed odds. Back one to win.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a lost race is sometimes run again.',
  jackpotRule: 'Long Shot wins twice in a row on your bet.',
  lockedHint: "(they're running)",
  create: () => ({ phase: 'idle', pick: 0, winner: 0, finish: [], startedAt: 0, shown: 0, bet: 0, takeout: TAKEOUT, returned: 0, longshotStreak: 0, lucky: false }),
  locked: (state) => state.phase === 'racing',
  options(state, env) {
    if (state.phase === 'racing') return [{ id: 'watch', label: 'Racing...' }];
    const takeout = TAKEOUT;
    return [
      { id: 'race', label: state.phase === 'done' ? 'Race again' : 'Race' },
      { id: 'horse', label: `${HORSES[state.pick]!.name} x${horseOdds(state.pick, takeout).toFixed(1)}` },
    ];
  },
  describe(state, env, highlighted) {
    const takeout = TAKEOUT;
    if (highlighted === 'horse' && state.phase !== 'racing') {
      // Six horses fill the pane; tapping to cycle is implied by the list.
      return HORSES.map((h, i) => `${state.pick === i ? '>' : ' '}${h.name.padEnd(10)} x${horseOdds(i, takeout).toFixed(1)}`).join('\n');
    }
    if (state.phase === 'racing') return track(state, state.shown);
    if (state.phase === 'done') {
      const net = state.returned - state.bet;
      // Result first: the track fills 6 of the pane's ~8 lines.
      // "★ Name +$X" when the ★'s re-run won it (kept inside 25 characters).
      const head = state.lucky ? `★ ${HORSES[state.winner]!.name}` : `${HORSES[state.winner]!.name} wins!`;
      return `${head} ${net > 0 ? `+$${net}` : `-$${state.bet}`}\n${track(state, BAR)}`;
    }
    return `Backing ${HORSES[state.pick]!.name}. Tap Race.`;
  },
  act(state, optionId, env) {
    if (state.phase === 'racing') return { state, bankroll: env.bankroll };
    if (optionId === 'horse') return { state: { ...state, pick: (state.pick + 1) % HORSES.length }, bankroll: env.bankroll };
    if (optionId !== 'race') return { state, bankroll: env.bankroll };
    // The ★'s luck may re-run a race your horse lost (only the re-run is shown).
    const q = secondChanceRate(horseOutcomes(state.pick), luckOf(env));
    const { outcome: winner, lucky, first } = drawWithLuck(env.rng, () => drawWinner(env.rng), (w) => w !== state.pick, q);
    // Losers finish somewhere between 55% and 95% of the way.
    const finishes = (w: number) => HORSES.map((_, i) => (i === w ? 1 : 0.55 + env.rng.next() * 0.4));
    const finish = finishes(winner);
    // A ★ second chance: the race you lost is shown first, then it's re-run
    // (the real race's own animation starts after those frames).
    const lost = first === undefined ? null : { ...state, pick: state.pick, finish: finishes(first) };
    const frames = lost === null ? [] : [
      ...Array.from({ length: BAR + 1 }, (_, step) => track(lost, step)),
      secondChanceFrame(`${HORSES[first!]!.name} won...`),
    ];
    return {
      state: {
        ...state,
        phase: 'racing',
        winner,
        finish,
        startedAt: env.now + frames.length * FRAME_MS,
        shown: 0,
        bet: env.bet,
        takeout: TAKEOUT,
        lucky,
        returned: 0,
      },
      bankroll: env.bankroll - env.bet,
      frames,
    };
  },
  tick(state, env) {
    if (state.phase !== 'racing') return null;
    const elapsed = env.now - state.startedAt;
    if (elapsed >= RACE_MS) return settle(state, env);
    const shown = Math.max(0, Math.floor((elapsed / RACE_MS) * BAR));
    return shown === state.shown ? null : { state: { ...state, shown }, bankroll: env.bankroll };
  },
};
